import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { FamilyMember } from './types';
import { INVOICE_STATUS_OPTIONS } from '../../../utils/invoiceStatuses';

export function buildPagination(totalItems: number, currentPage: number, itemsPerPage: number) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = Math.max(1, safePage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjStart = Math.max(1, end - 4);
  const visiblePages = Array.from({ length: end - adjStart + 1 }, (_, i) => adjStart + i);
  return {
    currentPage: safePage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
    visiblePages,
  };
}

export function paginate<T>(items: T[], page: number, itemsPerPage: number) {
  const startIdx = (page - 1) * itemsPerPage;
  return items.slice(startIdx, startIdx + itemsPerPage);
}

export async function fetchSponsorshipBadge(userCompanyId: string) {
  if (!userCompanyId || userCompanyId.trim() === '') {
    return { companyName: '', sponsorshipBadge: null as null | { enabled: boolean; text: string; logoUrl: string } };
  }

  try {
    const companyRef = doc(db, 'companies', userCompanyId);
    const companyDoc = await getDoc(companyRef);
    
    if (!companyDoc.exists()) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Company document not found for ID: ${userCompanyId}`);
      }
      return { companyName: '', sponsorshipBadge: null as null | { enabled: boolean; text: string; logoUrl: string } };
    }
    
    const companyData = companyDoc.data() as any;
    
    // Check if company has a name field
    if (!companyData || !companyData.name || companyData.name.trim() === '') {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Company document exists but has no name field for ID: ${userCompanyId}`);
      }
      return { companyName: '', sponsorshipBadge: null as null | { enabled: boolean; text: string; logoUrl: string } };
    }
    
    const sponsorshipSettings = companyData?.settings?.sponsorshipBadge;
    return {
      companyName: companyData.name.trim(),
      sponsorshipBadge: sponsorshipSettings ? {
        enabled: sponsorshipSettings.enabled || false,
        text: sponsorshipSettings.text || '',
        logoUrl: sponsorshipSettings.logoUrl || ''
      } : null,
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error fetching sponsorship badge:', error);
    }
    return { companyName: '', sponsorshipBadge: null as null | { enabled: boolean; text: string; logoUrl: string } };
  }
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
}

export async function getFamilyMembersForUser(currentUserId: string): Promise<FamilyMember[]> {
  const currentUserRef = doc(db, 'users', currentUserId);
  const currentUserDoc = await getDoc(currentUserRef);
  if (!currentUserDoc.exists()) return [];

  const currentUserData = currentUserDoc.data() as any;
  const familyMemberIds: string[] = currentUserData.familyMembers || [];
  if (familyMemberIds.length === 0) return [];

  const familyMembersRef = collection(db, 'users');
  const idChunks = chunkArray<string>(familyMemberIds, 30);
  const snapshots = await Promise.all(
    idChunks.map(ids => getDocs(query(familyMembersRef, where('__name__', 'in', ids))))
  );

  const members: FamilyMember[] = [];
  snapshots.forEach(snapshot => {
    snapshot.forEach(d => {
      const data = d.data() as any;
      const first = data.firstName || data.first_name || data.givenName || data.given_name || '';
      const last = data.lastName || data.last_name || data.familyName || data.family_name || '';
      const composed = `${first} ${last}`.trim();
      members.push({
        id: d.id,
        name: composed || data.name || (data.email ? String(data.email).split('@')[0] : ''),
        status: data.status || 'invited',
        createdAt: data.createdAt || 0,
        avatar: data.avatar || undefined,
        role: data.role,
        budget: data.budget || {},
        ...data,
      });
    });
  });

  members.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return members;
}

export async function getUserById(targetUserId: string): Promise<FamilyMember | null> {
  const userRef = doc(db, 'users', targetUserId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) return null;
  const userData = userDoc.data() as any;
  const firstNameValue = userData.firstName || userData.first_name || userData.givenName || userData.given_name || '';
  const lastNameValue = userData.lastName || userData.last_name || userData.familyName || userData.family_name || '';
  const composedName = `${firstNameValue} ${lastNameValue}`.trim();
  const fm: FamilyMember = {
    id: targetUserId,
    name: (composedName || (userData.name && String(userData.name).trim())
      || (userData.displayName && String(userData.displayName).trim())
      || (userData.email ? String(userData.email).split('@')[0] : 'Unknown User')),
    status: userData.status || 'active',
    createdAt: userData.createdAt || 0,
    avatar: userData.avatar || undefined,
    role: userData.role,
    budget: userData.budget || {},
    ...userData,
  };
  return fm;
}

export function filterDocuments(
  documents: any[],
  params: { search: string; status: string; category: string }
) {
  const { search, status, category } = params;
  const q = (search || '').toLowerCase();
  return documents.filter((document: any) => {
    const matchesSearch = !q ||
      document.title?.toLowerCase().includes(q) ||
      document.sender?.toLowerCase().includes(q) ||
      document.detectedDocumentCategory?.toLowerCase().includes(q);
    const matchesStatus = !status || document.status === status;
    const matchesCategory = !category || document.detectedDocumentCategory === category;
    return matchesSearch && matchesStatus && matchesCategory;
  });
}

export function buildStatusOptions() {
  return INVOICE_STATUS_OPTIONS;
}

export function buildCategoryOptions() {
  return [
    { value: 'Medical Invoice', label: 'Medical Invoice' },
    { value: 'Lab Report', label: 'Lab Report' },
    { value: 'Pharmacy Receipt', label: 'Pharmacy Receipt' },
    { value: 'Dental Invoice', label: 'Dental Invoice' },
    { value: 'Therapy Invoice', label: 'Therapy Invoice' }
  ];
}

export function formatDateInput(value: unknown): string {
  if (!value && value !== 0) return '';
  if (typeof value === 'number') {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }
  const s = String(value);
  if (/^\d{13}$/.test(s)) {
    const d = new Date(Number(s));
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }
  return s;
}

export function toMillis(value: string): number | undefined {
  if (!value) return undefined;
  const t = Date.parse(value);
  return isNaN(t) ? undefined : t;
}

export async function fetchUserLanguage(userId: string): Promise<string> {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  if (userDoc.exists()) {
    const userData = userDoc.data() as any;
    return userData.language || 'en';
  }
  return 'en';
}

export async function updateUserProfile(userId: string, form: {
  firstName: string;
  lastName: string;
  companyEmail: string;
}) {
  const ref = doc(db, 'users', userId);
  await updateDoc(ref, {
    firstName: form.firstName,
    lastName: form.lastName,
    companyEmail: form.companyEmail,
  });
}


